import React, { useEffect, useRef } from 'react';
import anime from 'animejs';
import './AnimatedBackground.css';

const AnimatedBackground = () => {
  const canvasRef = useRef(null);
  const shapesRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let particles = [];
    let geometricShapes = [];

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Particle system
    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 2 + 0.5;
        this.speedX = Math.random() * 0.5 - 0.25;
        this.speedY = Math.random() * 0.5 - 0.25;
        this.opacity = Math.random() * 0.5 + 0.2;
      }

      update() {
        this.x += this.speedX;
        this.y += this.speedY;

        if (this.x > canvas.width) this.x = 0;
        if (this.x < 0) this.x = canvas.width;
        if (this.y > canvas.height) this.y = 0;
        if (this.y < 0) this.y = canvas.height;
      }

      draw() {
        ctx.fillStyle = `rgba(102, 126, 234, ${this.opacity})`;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Geometric shapes
    class GeometricShape {
      constructor(type) {
        this.type = type;
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 60 + 40;
        this.rotation = Math.random() * Math.PI * 2;
        this.rotationSpeed = (Math.random() - 0.5) * 0.02;
        this.opacity = Math.random() * 0.3 + 0.1;
        this.pulsePhase = Math.random() * Math.PI * 2;
        this.color = this.getRandomColor();
      }

      getRandomColor() {
        const colors = [
          'rgba(102, 126, 234,',
          'rgba(118, 75, 162,',
          'rgba(240, 147, 251,',
          'rgba(79, 172, 254,'
        ];
        return colors[Math.floor(Math.random() * colors.length)];
      }

      update(time) {
        this.rotation += this.rotationSpeed;
        this.pulsePhase += 0.02;
      }

      draw(time) {
        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.rotate(this.rotation);
        
        const pulse = Math.sin(this.pulsePhase) * 0.2 + 1;
        const size = this.size * pulse;

        ctx.strokeStyle = `${this.color} ${this.opacity})`;
        ctx.lineWidth = 2;
        ctx.fillStyle = `${this.color} ${this.opacity * 0.3})`;

        switch(this.type) {
          case 'triangle':
            ctx.beginPath();
            ctx.moveTo(0, -size / 2);
            ctx.lineTo(size / 2, size / 2);
            ctx.lineTo(-size / 2, size / 2);
            ctx.closePath();
            break;
          case 'square':
            ctx.beginPath();
            ctx.rect(-size / 2, -size / 2, size, size);
            break;
          case 'circle':
            ctx.beginPath();
            ctx.arc(0, 0, size / 2, 0, Math.PI * 2);
            break;
          case 'hexagon':
            ctx.beginPath();
            for (let i = 0; i < 6; i++) {
              const angle = (Math.PI / 3) * i;
              const x = (size / 2) * Math.cos(angle);
              const y = (size / 2) * Math.sin(angle);
              if (i === 0) ctx.moveTo(x, y);
              else ctx.lineTo(x, y);
            }
            ctx.closePath();
            break;
        }

        ctx.fill();
        ctx.stroke();
        ctx.restore();
      }
    }

    // Initialize
    const numberOfParticles = Math.floor((canvas.width * canvas.height) / 15000);
    for (let i = 0; i < numberOfParticles; i++) {
      particles.push(new Particle());
    }

    const shapeTypes = ['triangle', 'square', 'circle', 'hexagon'];
    for (let i = 0; i < 12; i++) {
      const type = shapeTypes[Math.floor(Math.random() * shapeTypes.length)];
      geometricShapes.push(new GeometricShape(type));
    }

    // Connect particles
    const connectParticles = () => {
      const maxDistance = 120;
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < maxDistance) {
            const opacity = (1 - distance / maxDistance) * 0.3;
            ctx.strokeStyle = `rgba(102, 126, 234, ${opacity})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }
    };

    // Wave effect
    let waveOffset = 0;
    const drawWaves = () => {
      ctx.strokeStyle = 'rgba(102, 126, 234, 0.1)';
      ctx.lineWidth = 2;

      for (let i = 0; i < 3; i++) {
        ctx.beginPath();
        for (let x = 0; x <= canvas.width; x += 10) {
          const y = canvas.height * 0.7 + 
                    Math.sin((x + waveOffset + i * 100) * 0.01) * 50 +
                    Math.cos((x + waveOffset * 1.5 + i * 50) * 0.008) * 30;
          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
      }
      waveOffset += 1;
    };

    // Animation loop
    let time = 0;
    const animate = () => {
      ctx.fillStyle = 'rgba(10, 10, 10, 0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw waves
      drawWaves();
      
      // Update and draw geometric shapes
      geometricShapes.forEach(shape => {
        shape.update(time);
        shape.draw(time);
      });
      
      // Update and draw particles
      particles.forEach(particle => {
        particle.update();
        particle.draw();
      });
      
      // Connect particles
      connectParticles();
      
      time += 0.01;
      animationFrameId = requestAnimationFrame(animate);
    };

    // Initial fill
    ctx.fillStyle = '#0a0a0a';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  useEffect(() => {
    // Anime.js animations for DOM shapes
    anime({
      targets: '.floating-shape',
      translateY: () => anime.random(-50, 50),
      translateX: () => anime.random(-50, 50),
      rotate: () => anime.random(0, 360),
      scale: () => anime.random(0.8, 1.2),
      duration: () => anime.random(3000, 6000),
      easing: 'easeInOutSine',
      loop: true,
      direction: 'alternate',
      delay: (el, i) => i * 300
    });
  }, []);

  return (
    <div className="animated-background">
      <canvas ref={canvasRef} className="background-canvas" />
      
      {/* Additional floating CSS shapes */}
      <div className="floating-shapes-container">
        <div className="floating-shape shape-1"></div>
        <div className="floating-shape shape-2"></div>
        <div className="floating-shape shape-3"></div>
        <div className="floating-shape shape-4"></div>
        <div className="floating-shape shape-5"></div>
        <div className="floating-shape shape-6"></div>
      </div>
      
      {/* Gradient orbs */}
      <div className="gradient-orb orb-1"></div>
      <div className="gradient-orb orb-2"></div>
      <div className="gradient-orb orb-3"></div>
    </div>
  );
};

export default AnimatedBackground;
